import { Injectable, OnDestroy } from '@angular/core';
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import { Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { SubscriptionQueryService } from '~app/farp-odq/store/subscription/subscription-query.service';
import { AuthUser } from './auth-user.model';
import { ResetPassword, SessionFailure, SignIn, SignInCallback, SignInFailure, SignInSuccess, SignOut, SignOutSuccess } from './auth.actions';
import { AuthService } from './auth.service';

export class AuthStateModel {
  error?: any;
  loading = false;
  sessionStart: Date;
  user: AuthUser;
  isLoggedIn: boolean;
  tokenExpiring = 0;
  inactiveMemberForSubscription: boolean;
}

@State<AuthStateModel>({
  name: 'auth',
  defaults: new AuthStateModel(),
})
@Injectable()
export class AuthState implements NgxsOnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  constructor(private service: AuthService,
       private subscriptionService: SubscriptionQueryService) { }

  @Selector()
  static loading(state: AuthStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static signedIn(state: AuthStateModel): boolean {
    return !!state.isLoggedIn;
  }

  @Selector()
  static user(state: AuthStateModel): null | AuthUser {
    return state.user;
  }

  @Selector()
  static tokenExpiring(state: AuthStateModel): number {
    return state.tokenExpiring;
  }

  @Selector()
  static userIsInRole(state: AuthStateModel) {
    return (roles: string[]) => {
      return true;
    };
  }
  
  @Selector()
  static inactiveMemberForSubscription(state: AuthStateModel): boolean {
    return state.inactiveMemberForSubscription;
  } 

  ngxsOnInit({ getState, patchState, dispatch }: StateContext<AuthStateModel>) {
    this.service.userState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(usr => {
        const state = getState();
        if (!!usr) {
          dispatch(new SignInSuccess(usr));
        } else {
          if (!state.loading) {
            dispatch(new SignOutSuccess());
          }
        }
      });

    this.service.errors$.pipe(
      debounceTime(1000),
      filter(err => !!err),
      takeUntil(this.destroy$),
    )
      .subscribe(err => {
        const state = getState();
        if (!AuthState.signedIn(state) && !state.error) {
          dispatch(new SignInFailure(err));
        }

        if (AuthState.signedIn(state) && !state.error) {
          dispatch(new SessionFailure(err));
        }
      });

    this.service.loginChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoggedIn => {
        patchState({ isLoggedIn });
      });

    this.service.isLoggedIn()
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoggedIn => patchState({ isLoggedIn }));

    this.service.tokenExpiring$
      .pipe(takeUntil(this.destroy$))
      .subscribe(expireAt => {
        patchState({ tokenExpiring: expireAt });
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @Action(SignIn)
  onSignIn(
    { patchState, dispatch }: StateContext<AuthStateModel>,
    { returnUrl }: SignIn) {
    patchState({ loading: true });
    try {
      this.service.signIn(returnUrl);
    } catch (error) {
      dispatch(new SignInFailure(error));
    }
  }

  @Action(SignInCallback)
  onSignInCallback(
    { patchState }: StateContext<AuthStateModel>) {
    patchState({ loading: true });
  }

  @Action(SignInSuccess)
  onSignInSuccess(
    { patchState }: StateContext<AuthStateModel>,
    { user }: SignInSuccess,
  ) {
    this.subscriptionService.getSubscriptionProlongation(user.permitNumber)
    .subscribe( { next: (data)=> 
    { 
       patchState ({ inactiveMemberForSubscription: data.isInactiveMemberForSubscription,  user, loading: false  });
    }, error: (error) => {
      console.log('Error calling subscription prolongation ', error);
      patchState ({ inactiveMemberForSubscription: false,  user, loading: false})
    } });
 
  }

  @Action(SignInFailure)
  onSignInFailure({ patchState }: StateContext<AuthStateModel>, { error }: SignInFailure) {
    patchState({ user: null, error, loading: false });
  }

  @Action(SessionFailure)
  onSessionFailure({ patchState }: StateContext<AuthStateModel>, { error }: SessionFailure) {
    patchState({ user: null, error, loading: false });
  }

  @Action(SignOut)
  onSignOut({ getState, patchState }: StateContext<AuthStateModel>) {
    const wasSignedIn = AuthState.signedIn(getState());
    patchState({ loading: true });
    console.log('Auth.Satate.Service signout dispatch');
    this.service.signOut();
    if (!wasSignedIn) {
      patchState({ user: null, error: null, loading: false });
    }
  }

  @Action(SignOutSuccess)
  onSignOutSuccess({ patchState }: StateContext<AuthStateModel>) {
    patchState({ user: null, loading: false });
  }

  @Action(ResetPassword)
  onResetPassword(
    { patchState, dispatch }: StateContext<AuthStateModel>,
    { returnUrl }: ResetPassword) {
    patchState({ loading: true });
    try {
      this.service.resetPassword(returnUrl);
    } catch (error) {
      dispatch(new SignInFailure(error));
    }
  }
}
